import * as React from 'react'
import {
    FormConfig,
    FormErrorsType,
    useForm,
    type CreateSessionRequestBody,
} from '@/hooks'

import { Button } from '@atoms'
import { FormErrors } from '@/components/common/FormErrors/FormErrors'
import { ComponentProps } from 'react'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { Form, TextField, Checkbox } from '../Form'

type LoginFormProps = {
    onSubmit?: (payload: CreateSessionRequestBody) => void
    usernameInputLabel: string
    usernameInputPlaceholder: string
    passwordInputLabel: string
    passwordInputPlaceholder: string
    submitButtonLabel: string
    showPasswordInputLabel: string
    isPendingSubmit?: boolean
    errors?: FormErrorsType
}

export const LoginForm: React.FC<LoginFormProps> = (props) => {
    const {
        onSubmit,
        submitButtonLabel,
        usernameInputLabel,
        usernameInputPlaceholder,
        passwordInputLabel,
        passwordInputPlaceholder,
        showPasswordInputLabel,
        isPendingSubmit,
        errors,
    } = props

    const [parent] = useAutoAnimate()

    const formConfig = React.useMemo<FormConfig>(() => {
        return {
            username: {
                type: 'text',
                label: usernameInputLabel,
                placeholder: usernameInputPlaceholder,
                value: '',
                required: true,
            },
            password: {
                type: 'password',
                label: passwordInputLabel,
                placeholder: passwordInputPlaceholder,
                value: '',
                required: true,
            },
            showPassword: {
                type: 'checkbox',
                label: showPasswordInputLabel,
                value: false,
            },
        }
    }, [
        usernameInputLabel,
        usernameInputPlaceholder,
        passwordInputLabel,
        passwordInputPlaceholder,
        showPasswordInputLabel,
    ])

    const {
        getCheckboxProps,
        getInputProps,
        formState,
        isValid,
        setAllFieldsDirty,
    } = useForm(formConfig)

    const onFormSubmit: Required<ComponentProps<typeof Form>>['onSubmit'] =
        React.useCallback(
            (event) => {
                event.preventDefault()

                if (!isValid) {
                    setAllFieldsDirty()
                    return
                }

                const formData = new FormData(event.currentTarget)

                const data: CreateSessionRequestBody = {
                    username: formData.get('username') as string,
                    password: formData.get('password') as string,
                }

                onSubmit?.(data)
            },
            [isValid, onSubmit, setAllFieldsDirty]
        )

    const passwordFieldType = formState['showPassword'] ? 'text' : 'password'

    return (
        <div className="w-formPageFormWidth max-w-full">
            <div className="space-y-6" ref={parent}>
                <FormErrors errors={errors} />
                <Form onSubmit={onFormSubmit}>
                    <TextField {...getInputProps('username')} />
                    <TextField
                        {...getInputProps('password')}
                        type={passwordFieldType}
                    />
                    <Checkbox {...getCheckboxProps('showPassword')} />
                    <Button
                        name="loginFormSubtmit"
                        type="submit"
                        fullWidth
                        size="xlarge"
                        isLoading={isPendingSubmit}
                    >
                        {submitButtonLabel}
                    </Button>
                </Form>
            </div>
        </div>
    )
}
